/**
 * Rate Master v2.0 — generic React Query hooks factory.
 *
 * All 8 CRUD entities share the same list/get/update/delete/import shape
 * (see lib/rate-master/v2-api.ts). createEntityHooks() builds one typed
 * hook set per entity instead of hand-writing 8 near-identical files —
 * follows the same TanStack Query v5 pattern as hooks/useRateMaster.ts.
 */

'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { EntityClient } from '@/lib/rate-master/v2-api';
import type { ImportResponse, ListResponse } from '@/types/rate-master-v2';

export interface EntityListParams {
  limit?: number;
  offset?: number;
  is_active?: boolean;
  [key: string]: string | number | boolean | undefined;
}

export function createEntityHooks<TRecord, TUpdatePayload, TCreatePayload = never>(
  entityKey: string,
  client: EntityClient<TRecord, TUpdatePayload, TCreatePayload>,
) {
  const keys = {
    all: ['rate-master-v2', entityKey] as const,
    list: (params: EntityListParams) => [...keys.all, 'list', params] as const,
    detail: (id: string) => [...keys.all, 'detail', id] as const,
  };

  function useList(params: EntityListParams = {}) {
    return useQuery<ListResponse<TRecord>>({
      queryKey: keys.list(params),
      queryFn: () => client.list(params),
      staleTime: 2 * 60 * 1000,
    });
  }

  function useDetail(id: string | null) {
    return useQuery<TRecord>({
      queryKey: keys.detail(id ?? ''),
      queryFn: () => client.get(id as string),
      enabled: id !== null,
    });
  }

  function useCreate() {
    const queryClient = useQueryClient();
    return useMutation<TRecord, Error, TCreatePayload>({
      mutationFn: (payload: TCreatePayload) => client.create(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.all });
      },
    });
  }

  function useUpdate() {
    const queryClient = useQueryClient();
    return useMutation<TRecord, Error, { id: string; payload: TUpdatePayload }>({
      mutationFn: ({ id, payload }) => client.update(id, payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.all });
      },
    });
  }

  function useDelete() {
    const queryClient = useQueryClient();
    return useMutation<void, Error, string>({
      mutationFn: (id: string) => client.remove(id),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.all });
      },
    });
  }

  function useImport() {
    const queryClient = useQueryClient();
    return useMutation<ImportResponse, Error, File>({
      mutationFn: (file: File) => client.importFile(file),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.all });
      },
    });
  }

  return { keys, useList, useDetail, useCreate, useUpdate, useDelete, useImport };
}
